import { useEffect, useRef, useState } from 'react';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { customFetcher } from './useFetch';
import {
  mergeDebtorPool,
  missingDebtorIds,
} from '../services/debtorResolution.service';

/**
 * Lädt die Zahlungsschuldner einer Rechnung nach, die sich mit den vorhandenen
 * Mandantenlisten nicht auflösen lassen (QS #8).
 *
 * Betroffen ist der Schuldner, der über „Alle Mandanten" gewählt wurde und
 * nicht zum Vorgang gehört: nach dem Wiederöffnen stand er in
 * `Invoice.LegalClients`, aber in keiner geladenen Liste — der Chip blieb ohne
 * Namen, und beim nächsten Ändern der Auswahl fiel er still aus der Rechnung
 * heraus, weil das Auswahlfeld die sichtbaren Objekte zurückschreibt.
 *
 * Jede ID wird höchstens einmal angefragt, auch wenn der Abruf scheitert —
 * sonst würde ein gelöschter Mandant eine Dauerschleife auslösen.
 */
export const useResolvedDebtors = (
  selectedIds: number[] | undefined,
  pool: ILegalClient[] | undefined
): { extraDebtors: ILegalClient[]; isLoadingExtraDebtors: boolean } => {
  const [extraDebtors, setExtraDebtors] = useState<ILegalClient[]>([]);
  const [isLoadingExtraDebtors, setIsLoadingExtraDebtors] = useState(false);
  const requestedIds = useRef<Set<number>>(new Set());

  // Die schon geladenen Zusatz-Mandanten gehören mit in den Pool, sonst gilt
  // ihre ID weiter als fehlend und wird erneut angefragt.
  const stillMissing = missingDebtorIds(
    selectedIds,
    mergeDebtorPool(pool, extraDebtors)
  ).filter(id => !requestedIds.current.has(id));

  // Als String in die Abhängigkeiten, damit ein neues Array mit gleichem Inhalt
  // den Effekt nicht erneut auslöst.
  const missingKey = stillMissing.join(',');

  useEffect(() => {
    if (missingKey === '') {
      return;
    }
    const ids = missingKey.split(',').map(Number);
    ids.forEach(id => requestedIds.current.add(id));

    let cancelled = false;
    setIsLoadingExtraDebtors(true);

    Promise.all(
      ids.map(id =>
        customFetcher({ endpoint: `/legalclient/${id}` })
          .then(client => client as ILegalClient)
          // Ein Schuldner, der nicht mehr existiert, darf die anderen nicht
          // mitreissen — dann bleibt eben dieser eine ohne Namen.
          .catch(() => null)
      )
    ).then(results => {
      if (cancelled) return;
      const loaded = results.filter(
        (client): client is ILegalClient =>
          client !== null && typeof client?.idLegalClient === 'number'
      );
      if (loaded.length > 0) {
        setExtraDebtors(prev => mergeDebtorPool(prev, loaded));
      }
      setIsLoadingExtraDebtors(false);
    });

    return () => {
      cancelled = true;
    };
  }, [missingKey]);

  return { extraDebtors, isLoadingExtraDebtors };
};
